import React, { useMemo, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

// RiskAssess: VTE risk + bleeding-risk + recommendation in one question.
// Schema:
// {
//   questionType: 'RiskAssess',
//   questionTitle, questionText,
//   scoreScheme: 'NICE NG89 VTE',
//   riskFactorOptions: [{ id, label, isRiskFactor: true|false }],   // distractors allowed
//   bleedingFactorOptions: [{ id, label, isRiskFactor: true|false }], // distractors allowed
//   recommendationOptions: ['No prophylaxis', 'Mechanical only', 'LMWH', 'LMWH + mechanical'],
//   expectedRecommendation: 'LMWH + mechanical',
//   recommendationRationale: 'short explanation shown after submit'
// }

const RiskAssess = ({ question }) => {
  const riskOpts = useMemo(() => Array.isArray(question?.riskFactorOptions) ? question.riskFactorOptions : [], [question]);
  const bleedOpts = useMemo(() => Array.isArray(question?.bleedingFactorOptions) ? question.bleedingFactorOptions : [], [question]);
  const recOpts = useMemo(() => Array.isArray(question?.recommendationOptions) ? question.recommendationOptions : [], [question]);

  const [riskSelected, setRiskSelected] = useState([]);
  const [bleedSelected, setBleedSelected] = useState([]);
  const [recommendation, setRecommendation] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const toggle = (list, setList, id) => {
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  const expectedRisk = useMemo(() => riskOpts.filter((o) => o.isRiskFactor).map((o) => o.id), [riskOpts]);
  const expectedBleed = useMemo(() => bleedOpts.filter((o) => o.isRiskFactor).map((o) => o.id), [bleedOpts]);

  const score = useMemo(() => {
    if (!submitted) return null;
    const inSet = (set, id) => set.includes(id);
    const riskTP = riskSelected.filter((id) => inSet(expectedRisk, id)).length;
    const riskFP = riskSelected.filter((id) => !inSet(expectedRisk, id)).length;
    const riskFN = expectedRisk.filter((id) => !inSet(riskSelected, id)).length;
    const bleedTP = bleedSelected.filter((id) => inSet(expectedBleed, id)).length;
    const bleedFP = bleedSelected.filter((id) => !inSet(expectedBleed, id)).length;
    const bleedFN = expectedBleed.filter((id) => !inSet(bleedSelected, id)).length;
    const recCorrect = recommendation === question.expectedRecommendation;
    const overall = recCorrect && riskFN === 0 && riskFP === 0 && bleedFN === 0 && bleedFP === 0;
    return { riskTP, riskFP, riskFN, bleedTP, bleedFP, bleedFN, recCorrect, overall };
  }, [submitted, riskSelected, bleedSelected, recommendation, expectedRisk, expectedBleed, question]);

  const canSubmit = !submitted && recommendation.length > 0;

  const labelFor = (opts, id) => opts.find((o) => o.id === id)?.label || id;

  const renderOptionRow = (opt, list, setList, expectedSet) => {
    const checked = list.includes(opt.id);
    const isExpected = expectedSet.includes(opt.id);
    let bg = '';
    if (submitted) {
      if (checked && isExpected) bg = 'risk-assess__row--correct';
      else if (checked && !isExpected) bg = 'risk-assess__row--falsepos';
      else if (!checked && isExpected) bg = 'risk-assess__row--missed';
    }
    return (
      <Form.Check
        key={opt.id}
        type="checkbox"
        id={`${question.questionNumber}-${opt.id}`}
        className={`risk-assess__row ${bg}`}
        label={(
          <span>
            {opt.label}
            {submitted && isExpected ? <Badge bg="success" className="ms-2">expected</Badge> : null}
            {submitted && checked && !isExpected ? <Badge bg="danger" className="ms-2">distractor</Badge> : null}
          </span>
        )}
        checked={checked}
        disabled={submitted}
        onChange={() => toggle(list, setList, opt.id)}
      />
    );
  };

  return (
    <Container className="mt-3 p-3 bg-light text-dark rounded risk-assess-question">
      <h3 className="d-flex flex-wrap align-items-center gap-2">
        Q{question.questionNumber}: {question.questionTitle}
        {submitted && score ? (
          <Badge bg={score.overall ? 'success' : 'warning'} text={score.overall ? 'light' : 'dark'}>
            {score.overall ? 'All correct' : 'Partial'}
          </Badge>
        ) : null}
      </h3>
      <hr />
      <p className="mb-2">{question.questionText}</p>
      {question.scoreScheme ? <div className="text-muted small mb-3">Scheme: {question.scoreScheme}</div> : null}

      <Row className="g-3">
        {riskOpts.length > 0 ? (
          <Col xs={12} md={6}>
            <h6 className="mb-2">VTE risk factors</h6>
            {riskOpts.map((opt) => renderOptionRow(opt, riskSelected, setRiskSelected, expectedRisk))}
            {submitted && score ? (
              <div className="small text-muted mt-2">
                Recall {score.riskTP}/{expectedRisk.length} · False positives {score.riskFP}
              </div>
            ) : null}
          </Col>
        ) : null}
        {bleedOpts.length > 0 ? (
          <Col xs={12} md={6}>
            <h6 className="mb-2">Bleeding risk factors</h6>
            {bleedOpts.map((opt) => renderOptionRow(opt, bleedSelected, setBleedSelected, expectedBleed))}
            {submitted && score ? (
              <div className="small text-muted mt-2">
                Recall {score.bleedTP}/{expectedBleed.length} · False positives {score.bleedFP}
              </div>
            ) : null}
          </Col>
        ) : null}
      </Row>

      {recOpts.length > 0 ? (
        <div className="mt-3">
          <h6 className="mb-2">Final recommendation</h6>
          {recOpts.map((opt) => {
            const isSelected = recommendation === opt;
            const isExpected = submitted && question.expectedRecommendation === opt;
            let variant = isSelected ? 'primary' : 'outline-primary';
            if (submitted) {
              if (isExpected) variant = 'success';
              if (isSelected && !isExpected) variant = 'outline-danger';
            }
            return (
              <Button
                key={opt}
                type="button"
                variant={variant}
                size="sm"
                className="me-2 mb-2"
                disabled={submitted}
                onClick={() => setRecommendation(opt)}
              >
                {opt}{submitted && isExpected ? ' ✓' : ''}
              </Button>
            );
          })}
          {submitted && score ? (
            <div className="small mt-2">
              <Badge bg={score.recCorrect ? 'success' : 'danger'}>
                Recommendation {score.recCorrect ? 'correct' : 'incorrect'}
              </Badge>
            </div>
          ) : null}
        </div>
      ) : null}

      {!submitted ? (
        <Button
          type="button"
          variant="primary"
          className="mt-3"
          disabled={!canSubmit}
          onClick={() => setSubmitted(true)}
        >
          Submit risk assessment
        </Button>
      ) : (
        question.recommendationRationale ? (
          <div className="mt-3 p-2 bg-white rounded border small">
            <strong>Rationale:</strong> {question.recommendationRationale}
          </div>
        ) : null
      )}
    </Container>
  );
};

export default RiskAssess;
